// Configuración de pago a repartidores (nivel operador). Escribe
// /billing/driver-payment-config (PUT crea una versión nueva). El repartidor ve el total
// del día calculado con esta misma config (reconciliación con la liquidación).
//
// ── Organización de la pantalla ─────────────────────────────────────────────────────
// Antes era un scroll plano de 13 bloques con ~520 palabras de explicación permanente:
// todo pesaba lo mismo y el monto por defecto —que define el pago de toda la flota—
// ocupaba el mismo espacio visual que la cuenta bancaria.
//
// Ahora son tarjetas por tema, ordenadas de lo que TODOS configuran a lo que casi nadie
// toca, y las tres últimas nacen plegadas con un contador de lo que hay dentro. La
// explicación pasó a iconos ⓘ (InfoHint): el texto que dice POR QUÉ se pide bajo
// demanda; el que hace falta para completar el campo se queda visible.
//
// La cascada de tarifación ya no se explica, se muestra: el panel "Cómo queda una
// entrega" simula el monto en vivo con lo que hay en pantalla, incluso sin guardar.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import {
  Save,
  RefreshCw,
  Coins,
  MapPin,
  PackageOpen,
  Split,
  Receipt,
  Activity,
  ChevronDown,
  PackageCheck,
} from "lucide-react";
import { Button } from "../../../../components/ui/Button";
import { InfoHint } from "../../../../components/ui/InfoHint";
import { Select } from "../../../../components/ui/Select";
import { Switch } from "../../../../components/ui/Switch";
import {
  getDriverPaymentConfig,
  saveDriverPaymentConfig,
  getDriverPaymentCoverageCheck,
} from "../../../../api/billing";
import ComunaZonesEditor, {
  countIncompleteZoneRows,
  zonesFromApi,
  zonesToApi,
} from "../../billing/components/ComunaZonesEditor";
import PostalCodeZonesEditor, {
  countIncompletePostalRows,
  postalZonesFromApi,
  postalZonesToApi,
} from "../../billing/components/PostalCodeZonesEditor";
import FleetLeadersEditor, {
  countIncompleteFleetRows,
  fleetLeadersFromApi,
  fleetLeadersToApi,
} from "./FleetLeadersEditor";
import DriverPaymentPreview from "./DriverPaymentPreview";
import {
  DRIVER_VEHICLE_ORDER,
  DRIVER_VEHICLE_OPTIONS,
} from "../../../../utils/driverVehicleTypes";
import { useCountry } from "../../../../context/CountryContext";
import { CHILE_BANKS } from "../../../../utils/chileBanks";
import { cn } from "../../../../utils/cn";

const inputCls =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

const SIZE_CATEGORIES = ["XL", "2XL", "3XL"];
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Montos base por vehículo como mapa vehicleType -> string (los 4 tipos seleccionables).
const emptyVehicleRates = () =>
  Object.fromEntries(DRIVER_VEHICLE_ORDER.map((v) => [v, ""]));

// API (vehicleRates[]) -> mapa de strings para el formulario.
const vehicleRatesFromApi = (apiRates) => {
  const base = emptyVehicleRates();
  (Array.isArray(apiRates) ? apiRates : []).forEach((r) => {
    if (r && r.vehicleType != null && base[r.vehicleType] !== undefined) {
      base[r.vehicleType] = r.baseCost != null ? String(r.baseCost) : "";
    }
  });
  return base;
};

// Mapa -> API. Solo los tipos con monto entero > 0; sin duplicados (una entrada por tipo).
const vehicleRatesToApi = (map) =>
  DRIVER_VEHICLE_ORDER.map((v) => ({
    vehicleType: v,
    baseCost: Math.round(num(map[v])),
  })).filter((r) => r.baseCost > 0);

// Recargos por categoría como mapa category -> { multiplier, additionalCost } (strings).
const emptySurcharges = () =>
  Object.fromEntries(
    SIZE_CATEGORIES.map((c) => [c, { multiplier: "1", additionalCost: "0" }]),
  );

const surchargesFromApi = (apiSurcharges) => {
  const base = emptySurcharges();
  (Array.isArray(apiSurcharges) ? apiSurcharges : []).forEach((s) => {
    const cat = String(s.category || "").toUpperCase();
    if (base[cat]) {
      base[cat] = {
        multiplier: s.multiplier != null ? String(s.multiplier) : "1",
        additionalCost: s.additionalCost != null ? String(s.additionalCost) : "0",
      };
    }
  });
  return base;
};

// Solo se envían las categorías con recargo real (×>1 o +>0).
const surchargesToApi = (map) =>
  SIZE_CATEGORIES.map((cat) => ({
    category: cat,
    multiplier: num(map[cat]?.multiplier) || 1,
    additionalCost: Math.round(num(map[cat]?.additionalCost)),
  })).filter((s) => s.multiplier !== 1 || s.additionalCost !== 0);

/**
 * Tarjeta de una sección. Espeja el baseline visual de `SectionCard` (mismo borde, radio,
 * sombra y círculo de icono) y le agrega plegado.
 *
 * Las secciones plegables nacen CERRADAS y muestran en la cabecera cuántas reglas
 * contienen: sin ese contador, plegar esconde información y el operador no puede saber
 * si adentro hay algo sin abrirlas una por una.
 */
function Panel({
  icon: Icon,
  title,
  subtitle,
  hint,
  badge,
  collapsible = false,
  defaultOpen = false,
  children,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(collapsible ? defaultOpen : true);
  const isOpen = collapsible ? open : true;

  const header = (
    <div className="flex items-start gap-3 min-w-0 text-left flex-1">
      <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-fg flex items-center gap-1.5">
          {title}
          {hint && (
            <InfoHint label={hint} srLabel={t("Sobre {{s}}", { s: title })} />
          )}
        </h3>
        {subtitle && <p className="text-sm text-fg-muted mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );

  return (
    <section className="ui-section-card rounded-lg border border-border bg-bg-muted p-5 space-y-4 shadow-card">
      {collapsible ? (
        <div className="flex items-start justify-between gap-3">
          {header}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={isOpen}
            className="shrink-0 inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm text-fg-muted hover:text-fg hover:bg-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 cursor-pointer transition-colors"
          >
            {badge != null && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-bg text-xs font-medium text-fg border border-border">
                {badge}
              </span>
            )}
            {isOpen ? t("Ocultar") : t("Mostrar")}
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform motion-reduce:transition-none",
                isOpen && "rotate-180",
              )}
              aria-hidden="true"
            />
          </button>
        </div>
      ) : (
        <div className="flex items-start gap-3">{header}</div>
      )}
      {isOpen && <div className="space-y-4">{children}</div>}
    </section>
  );
}

// `company` es el documento de la empresa logística que AdminSettings ya le pasa a todas
// sus secciones. Acá se usa para una sola cosa: la cobertura (`coverageAreas`) con la que
// se acotan los selectores de tarifa por comuna. `loadingCompany` distingue "todavía no
// llegó" de "llegó y no hay cobertura" — ver el contrato de ComunaZonesEditor.
export default function DriverPaymentSection({ company, loadingCompany = false }) {
  const { t } = useTranslation();
  const { locale, labels, postalCodeRates, countryCode } = useCountry();
  // Tarifas por código postal: solo en países que las habilitan (hoy Argentina). Un
  // operador chileno no ve la sección y su payload queda idéntico al de siempre.
  const postalEnabled = postalCodeRates?.enabled === true;
  const communeWord = labels?.commune ?? "Comuna";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(null);

  const [defaultBaseCost, setDefaultBaseCost] = useState("");
  // Moneda de liquidación: SOLO LECTURA. La resuelve el backend desde el país de
  // operación de la empresa y queda congelada en la versión; acá solo se muestra.
  const [currency, setCurrency] = useState(null);
  const [taxMode, setTaxMode] = useState("exclusive");
  // Tasa de IVA de la liquidación, como FRACCIÓN (0.19 = 19%). SOLO LECTURA, igual que la
  // moneda: la fija el país de operación y el backend la resuelve.
  const [taxRate, setTaxRate] = useState(null);
  const [originAccount, setOriginAccount] = useState("");
  // Banco (código de plaza SBIF) de la cuenta origen: define el formato por defecto de la
  // planilla de pago masivo. Solo se muestra/acepta para operadores de Chile.
  const [originBankCode, setOriginBankCode] = useState("");
  const [zones, setZones] = useState([]); // filas UI del editor de comunas
  const [postalZones, setPostalZones] = useState([]); // filas UI del editor de CP
  const [surcharges, setSurcharges] = useState(emptySurcharges());
  const [adjustPickAndGoByCommune, setAdjustPickAndGoByCommune] = useState(true);
  // Modo de pago: 'single' (a todos igual) | 'byVehicle' (base por tipo de vehículo).
  const [paymentMode, setPaymentMode] = useState("single");
  const [vehicleRates, setVehicleRates] = useState(emptyVehicleRates());
  const [fleetLeaders, setFleetLeaders] = useState([]);
  const [savedLeaderIds, setSavedLeaderIds] = useState([]);
  // Pago de retiros realizados (recolecciones). Opt-in versionado en billing: un día paga
  // retiros solo si la versión de config que lo resuelve lo tiene activo.
  const [includePickups, setIncludePickups] = useState(false);
  // Nombres de los líderes, que los publica el editor de flotas cuando carga su
  // directorio: el simulador los necesita para rotular su selector de flota.
  const [leaderNames, setLeaderNames] = useState({});
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  // Instantánea del payload tal como quedó guardado. Es la referencia contra la que se
  // detecta "cambios sin guardar": comparar el payload (y no el estado del formulario)
  // evita falsos positivos por cosas que no viajan, como una fila a medio completar.
  const [savedPayload, setSavedPayload] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getDriverPaymentConfig()
      .then((res) => {
        if (!alive) return;
        const cfg = res?.data ?? null;
        if (cfg) {
          setVersion(cfg.version ?? null);
          setDefaultBaseCost(cfg.defaultBaseCost != null ? String(cfg.defaultBaseCost) : "");
          setCurrency(cfg.currency ?? null);
          setTaxMode(cfg.taxMode || "exclusive");
          setTaxRate(cfg.taxRate != null ? Number(cfg.taxRate) : null);
          setOriginAccount(cfg.originAccount != null ? String(cfg.originAccount) : "");
          setOriginBankCode(cfg.originBankCode != null ? String(cfg.originBankCode) : "");
          setZones(zonesFromApi(cfg.zones, cfg.defaultBaseCost));
          setPostalZones(postalZonesFromApi(cfg.postalCodeZones));
          setSurcharges(surchargesFromApi(cfg.sizeSurcharges));
          setPaymentMode(cfg.paymentMode === "byVehicle" ? "byVehicle" : "single");
          setVehicleRates(vehicleRatesFromApi(cfg.vehicleRates));
          setAdjustPickAndGoByCommune(cfg.adjustPickAndGoByCommune !== false);
          setFleetLeaders(fleetLeadersFromApi(cfg.fleetLeaders));
          setSavedLeaderIds((cfg.fleetLeaders ?? []).map((f) => String(f.driverId)));
          setIncludePickups(cfg.includePickups === true);
        }
      })
      .catch(() => {
        /* sin config previa: formulario vacío */
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // Cobertura del operador para acotar los selectores del editor de comunas. `null` (y no
  // `[]`) mientras el documento de la empresa no llegó o falló.
  const coverageAreas = company ? (company.coverageAreas ?? []) : null;

  const defaultNum = Math.round(num(defaultBaseCost));
  const byVehicle = paymentMode === "byVehicle";
  const taxRateLabel =
    taxRate != null ? `${Math.round(Number(taxRate) * 10000) / 100}%` : null;

  const vehicleBaseCosts = useMemo(
    () =>
      Object.fromEntries(
        DRIVER_VEHICLE_ORDER.map((v) => {
          const r = Math.round(num(vehicleRates[v]));
          return [v, r > 0 ? r : defaultNum];
        }),
      ),
    [vehicleRates, defaultNum],
  );

  const setVehicleRate = (v, value) =>
    setVehicleRates((m) => ({ ...m, [v]: value }));

  const setSurcharge = (cat, patch) =>
    setSurcharges((m) => ({ ...m, [cat]: { ...m[cat], ...patch } }));

  /**
   * Payload EXACTO del PUT. Lo comparten el guardado, la detección de cambios y el
   * simulador: así los tres hablan del mismo objeto y el simulador no puede mostrar una
   * cifra calculada con datos que no se van a enviar.
   */
  const payload = useMemo(() => {
    let apiZones = zonesToApi(zones);
    if (!byVehicle) {
      apiZones = apiZones.map(({ vehicleTypes, ...rest }) => rest);
    }
    const p = {
      defaultBaseCost: defaultNum,
      paymentMode,
      zones: apiZones,
      sizeSurcharges: surchargesToApi(surcharges),
      // Lo único del IVA que elige el operador. Ni `taxRate` ni `currency` ni
      // `originCurrency` viajan: los tres dependen del país de operación y los resuelve
      // el backend, que es donde tiene que vivir un dato que liquida dinero.
      taxMode,
      adjustPickAndGoByCommune,
      originAccount: originAccount.trim(),
    };
    // El banco de la cuenta origen solo viaja si es Chile y hay un código elegido: la
    // clave se OMITE cuando está vacía (patrón `postalCodeZones`/`fleetLeaders`), así un
    // operador chileno que no lo configura manda el MISMO payload que antes.
    if (countryCode === "CL" && originBankCode.trim()) {
      p.originBankCode = originBankCode.trim();
    }
    if (byVehicle) p.vehicleRates = vehicleRatesToApi(vehicleRates);
    // `postalCodeZones` y `fleetLeaders` se OMITEN cuando no hay reglas: el payload de un
    // operador que no las usa queda byte-idéntico al de antes y no genera versiones
    // espurias en una config de dinero versionada.
    if (postalEnabled) {
      let apiPostalZones = postalZonesToApi(postalZones);
      if (!byVehicle) {
        apiPostalZones = apiPostalZones.map(({ vehicleTypes, ...rest }) => rest);
      }
      if (apiPostalZones.length > 0) p.postalCodeZones = apiPostalZones;
    }
    const apiFleetLeaders = fleetLeadersToApi(fleetLeaders);
    if (apiFleetLeaders.length > 0) p.fleetLeaders = apiFleetLeaders;
    // `includePickups` viaja SOLO en true: billing guarda el false como AUSENTE, así que
    // omitirlo deja el payload de quien no paga retiros idéntico al de antes (sin
    // versiones espurias) y apagarlo vuelve exactamente a ese payload.
    if (includePickups) p.includePickups = true;
    return p;
  }, [
    zones, byVehicle, defaultNum, paymentMode, surcharges, taxMode,
    adjustPickAndGoByCommune, originAccount, originBankCode, countryCode,
    vehicleRates, postalEnabled, postalZones, fleetLeaders, includePickups,
  ]);

  // Config que consume el simulador: el payload + los dos valores de solo lectura que el
  // backend resuelve por país y que no viajan en él.
  const previewConfig = useMemo(
    () => ({ ...payload, taxRate: taxRate ?? 0.19 }),
    [payload, taxRate],
  );

  const dirty = savedPayload != null && JSON.stringify(payload) !== savedPayload;
  const zoneCount = payload.zones.length + (payload.postalCodeZones?.length ?? 0);
  const exceptionCount =
    (payload.fleetLeaders?.length ?? 0) + (adjustPickAndGoByCommune ? 0 : 1);

  // Instantánea inicial una vez que terminó la carga: antes de eso todo se vería sucio.
  //
  // El guardia es un ref y NO `savedPayload != null`: el estado se compromete de forma
  // asíncrona, así que un segundo disparo del efecto (el payload es una dependencia)
  // podía encontrarlo todavía en null y pisar la instantánea con el valor YA editado —
  // con lo que el primer cambio del usuario no se detectaba. El ref se actualiza en el
  // acto y cierra esa ventana.
  const snapshotTaken = useRef(false);
  useEffect(() => {
    if (loading || snapshotTaken.current) return;
    snapshotTaken.current = true;
    setSavedPayload(JSON.stringify(payload));
  }, [loading, payload]);

  const handleSave = async () => {
    if (!defaultBaseCost || defaultNum <= 0) {
      toast.error(t("El monto por defecto es obligatorio y debe ser mayor a 0."));
      return;
    }
    // Aviso (no bloqueante) de las filas que el guardado descartaría: sin destino/monto.
    // Antes se descartaban en silencio y el operador creía haber guardado una tarifa que
    // nunca existió.
    const incomplete =
      countIncompleteZoneRows(zones) +
      countIncompleteFleetRows(fleetLeaders) +
      (postalEnabled ? countIncompletePostalRows(postalZones) : 0);
    if (incomplete > 0) {
      toast.warn(
        t("{{count}} regla incompleta se descartará al guardar (falta el destino o el monto).", {
          count: incomplete,
        }),
      );
    }
    setSaving(true);
    try {
      const res = await saveDriverPaymentConfig(payload);
      const cfg = res?.data;
      if (cfg?.version != null) setVersion(cfg.version);
      // Moneda e IVA los decide el backend: se reflejan los que quedaron en la versión.
      if (cfg?.currency) setCurrency(cfg.currency);
      if (cfg?.taxRate != null) setTaxRate(Number(cfg.taxRate));
      setSavedLeaderIds((cfg?.fleetLeaders ?? []).map((f) => String(f.driverId)));
      setSavedPayload(JSON.stringify(payload));
      toast.success(t("Configuración de pago guardada."));
    } catch (err) {
      toast.error(err.response?.data?.message ?? t("No se pudo guardar la configuración."));
    } finally {
      setSaving(false);
    }
  };

  const loadCoverage = async () => {
    setCoverageLoading(true);
    try {
      const res = await getDriverPaymentCoverageCheck(30);
      setCoverage(res?.data ?? null);
    } catch (err) {
      toast.error(err.response?.data?.message ?? t("No se pudo cargar la cobertura."));
    } finally {
      setCoverageLoading(false);
    }
  };

  const fmt = (n) =>
    new Intl.NumberFormat(locale, { style: "currency", currency: currency || "CLP", maximumFractionDigits: 0 }).format(n || 0);

  if (loading) {
    return <div className="text-sm text-fg-muted">{t("Cargando…")}</div>;
  }

  return (
    <div className="flex flex-col gap-4 max-w-[64rem]">
      <div className="flex flex-col lg:flex-row gap-5 items-start">
        {/* Simulador. En móvil va PRIMERO (es el resumen de lo que se está armando); en
            escritorio queda a la derecha y fijo mientras se recorre el formulario. */}
        <aside className="w-full lg:w-72 shrink-0 lg:order-2 lg:sticky lg:top-4">
          <DriverPaymentPreview
            config={previewConfig}
            currency={currency || "CLP"}
            locale={locale}
            communeWord={communeWord}
            fleetLeaderNames={leaderNames}
          />
        </aside>

        <div className="flex-1 min-w-0 w-full lg:order-1 flex flex-col gap-4">
          {/* 1 · Tarifa base — lo que TODOS configuran. */}
          <Panel
            icon={Coins}
            title={t("Tarifa base")}
            subtitle={t("Lo que se paga por una entrega normal.")}
          >
            <div>
              <span className="text-sm font-medium text-fg">{t("Modo de pago")}</span>
              <div className="inline-flex rounded-md border border-border overflow-hidden h-9 mt-2">
                <button
                  type="button"
                  onClick={() => setPaymentMode("single")}
                  className={cn(
                    "px-3 text-sm cursor-pointer transition-colors",
                    !byVehicle ? "bg-brand-500 text-white" : "bg-bg text-fg-muted hover:text-fg",
                  )}
                >
                  {t("Único")}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMode("byVehicle")}
                  className={cn(
                    "px-3 text-sm border-l border-border cursor-pointer transition-colors",
                    byVehicle ? "bg-brand-500 text-white" : "bg-bg text-fg-muted hover:text-fg",
                  )}
                >
                  {t("Por tipo de vehículo")}
                </button>
              </div>
            </div>

            <div>
              {/* El InfoHint va FUERA del <label>: un botón dentro de un label hereda su
                  texto como nombre accesible (y en varios navegadores el clic activa el
                  input asociado), así que quedaban dos elementos con el mismo nombre. */}
              <div className="flex items-center gap-1.5">
                <label
                  htmlFor="dp-default-base"
                  className="text-sm font-medium text-fg"
                >
                  {t("Monto por defecto por entrega")}
                  <span className="text-status-error" aria-hidden="true"> *</span>
                </label>
                <InfoHint
                  srLabel={t("Sobre el monto por defecto")}
                  label={
                    byVehicle
                      ? t("Se paga en toda comuna sin tarifa propia, y es el fallback de los repartidores sin vehículo o de los tipos sin monto. Es obligatorio: así el repartidor nunca ve $0.")
                      : t("Se paga en toda comuna sin tarifa propia. Es obligatorio: así el repartidor nunca ve $0.")
                  }
                />
              </div>
              <input
                id="dp-default-base"
                type="number"
                min="1"
                className={`${inputCls} max-w-[220px] mt-1`}
                value={defaultBaseCost}
                onChange={(e) => setDefaultBaseCost(e.target.value)}
                placeholder={t("Ej: 1500")}
              />
            </div>

            {byVehicle && (
              <div>
                <span className="text-sm font-medium text-fg flex items-center gap-1.5">
                  {t("Monto base por tipo de vehículo")}
                  <InfoHint
                    srLabel={t("Sobre los montos por vehículo")}
                    label={t("Opcional por tipo. Los que dejes vacíos —y los repartidores sin vehículo asignado— usan el monto por defecto.")}
                  />
                </span>
                <div className="flex flex-col gap-2 mt-2">
                  {DRIVER_VEHICLE_OPTIONS.map((opt) => (
                    <div key={opt.value} className="flex items-center gap-2">
                      <label
                        htmlFor={`dp-vehicle-${opt.value}`}
                        className="inline-flex items-center w-24 h-9 px-2 rounded-md border border-border bg-bg-subtle text-sm font-medium"
                      >
                        {t(opt.labelKey)}
                      </label>
                      <input
                        id={`dp-vehicle-${opt.value}`}
                        type="number"
                        min="1"
                        className={`${inputCls} max-w-[180px]`}
                        value={vehicleRates[opt.value]}
                        onChange={(e) => setVehicleRate(opt.value, e.target.value)}
                        placeholder={t("Usa el monto por defecto")}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          {/* 2 · Ajustes por destino. */}
          <Panel
            icon={MapPin}
            title={t("Ajustes por destino")}
            subtitle={t("Comunas que pagan más o menos que el monto por defecto.")}
            hint={t("El ajuste se suma o descuenta sobre el monto por defecto, con piso en $0. Una comuna sin regla propia paga el monto por defecto.")}
            collapsible
            defaultOpen={zoneCount > 0}
            badge={zoneCount}
          >
            <ComunaZonesEditor
              rows={zones}
              onChange={setZones}
              currency={currency || "CLP"}
              baseCost={defaultNum}
              vehicleFilter={byVehicle}
              vehicleBaseCosts={byVehicle ? vehicleBaseCosts : null}
              coverageAreas={coverageAreas}
              coverageLoading={loadingCompany}
            />
            {/* Las tarifas por CP van DESPUÉS de las comunas porque le GANAN: un CP
                configurado pisa la tarifa del municipio de destino. */}
            {postalEnabled && (
              <PostalCodeZonesEditor
                rows={postalZones}
                onChange={setPostalZones}
                currency={currency || "CLP"}
                baseCost={defaultNum}
                vehicleFilter={byVehicle}
                vehicleBaseCosts={byVehicle ? vehicleBaseCosts : null}
              />
            )}
          </Panel>

          {/* 3 · Recargo por tamaño. Son tres filas: no vale la pena plegarlo. */}
          <Panel
            icon={PackageOpen}
            title={t("Recargo por sobredimensionado")}
            subtitle={t("Lo que se suma cuando el paquete es XL, 2XL o 3XL.")}
            hint={t("Se aplica sobre la tarifa ya ajustada por comuna: monto = base × multiplicador + fijo. Un multiplicador de 2 paga el doble; el fijo suma encima.")}
          >
            <div className="flex flex-col gap-2">
              {SIZE_CATEGORIES.map((cat) => {
                const s = surcharges[cat] ?? {};
                const unit = Math.round(defaultNum * (num(s.multiplier) || 1) + Math.round(num(s.additionalCost)));
                return (
                  <div key={cat} className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center justify-center w-12 h-9 rounded-md border border-border bg-bg-subtle text-sm font-medium">
                      {cat}
                    </span>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      className={`${inputCls} max-w-[120px]`}
                      value={s.multiplier ?? "1"}
                      onChange={(e) => setSurcharge(cat, { multiplier: e.target.value })}
                      placeholder={t("×Mult.")}
                      aria-label={t("{{c}}: multiplicador sobre la base", { c: cat })}
                    />
                    <input
                      type="number"
                      min="0"
                      className={`${inputCls} max-w-[120px]`}
                      value={s.additionalCost ?? "0"}
                      onChange={(e) => setSurcharge(cat, { additionalCost: e.target.value })}
                      placeholder={t("+ Fijo")}
                      aria-label={t("{{c}}: monto fijo adicional", { c: cat })}
                    />
                    <span className="text-xs text-fg-muted tabular-nums">
                      {t("Ej. por defecto")}: {fmt(unit)}
                    </span>
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* 4 · Retiros realizados. Rótulo explícito "(recolecciones)": en Excepciones,
              "retiros pick & go" son ENTREGAS pick & go, y los dos no se pueden confundir. */}
          <Panel
            icon={PackageCheck}
            title={t("Retiros realizados")}
            subtitle={t("Lo que se suma por cada retiro en una recolección.")}
          >
            {/* El InfoHint va FUERA del <label> que arma el Switch (ver "Monto por
                defecto por entrega"). */}
            <div className="flex items-center gap-1.5">
              <Switch
                id="dp-include-pickups"
                checked={includePickups}
                onChange={setIncludePickups}
                label={t("Pagar retiros realizados (recolecciones)")}
              />
              <InfoHint
                srLabel={t("Sobre el pago de retiros realizados")}
                label={t("Suma a cada día el monto configurado en cada parada de recolección (Recolecciones → Pagar repartidor) por cada retiro realizado. Aplica desde el día en que guardes; los días anteriores no cambian. Lleva IVA o retención igual que las entregas.")}
              />
            </div>
          </Panel>

          {/* 5 · Excepciones: las dos reglas que se saltan la cascada. */}
          <Panel
            icon={Split}
            title={t("Excepciones")}
            subtitle={t("Casos que no siguen la tarifa de arriba.")}
            collapsible
            defaultOpen={exceptionCount > 0}
            badge={exceptionCount}
          >
            <div>
              <span className="text-sm font-medium text-fg flex items-center gap-1.5">
                {t("¿Los retiros pick & go se ajustan por comuna?")}
                <InfoHint
                  srLabel={t("Sobre los retiros pick & go")}
                  label={t("En pick & go el repartidor entrega directo, sin volver a bodega. Si eliges No, esos paquetes pagan el monto por defecto sin ajuste por comuna; el recargo por sobredimensionado igual aplica.")}
                />
              </span>
              <div className="flex gap-4 mt-2">
                <label className="inline-flex items-center gap-2 text-sm text-fg cursor-pointer">
                  <input
                    type="radio"
                    name="adjustPickAndGoByCommune"
                    checked={adjustPickAndGoByCommune}
                    onChange={() => setAdjustPickAndGoByCommune(true)}
                  />
                  {t("Sí")}
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-fg cursor-pointer">
                  <input
                    type="radio"
                    name="adjustPickAndGoByCommune"
                    checked={!adjustPickAndGoByCommune}
                    onChange={() => setAdjustPickAndGoByCommune(false)}
                  />
                  {t("No")}
                </label>
              </div>
            </div>

            <div className="border-t border-border/60 pt-4">
              <FleetLeadersEditor
                rows={fleetLeaders}
                onChange={setFleetLeaders}
                currency={currency || "CLP"}
                locale={locale}
                savedLeaderIds={savedLeaderIds}
                onDirectoryLoaded={setLeaderNames}
              />
            </div>
          </Panel>

          {/* 6 · Impuestos y cuenta. Casi nadie lo toca: nace plegado. */}
          <Panel
            icon={Receipt}
            title={t("Impuestos y cuenta de pago")}
            subtitle={
              taxRateLabel
                ? t("{{cur}} · IVA {{rate}} · {{mode}}", {
                    cur: currency || t("Sin definir"),
                    rate: taxRateLabel,
                    mode: taxMode === "inclusive" ? t("IVA incluido") : t("montos netos"),
                  })
                : t("Moneda e IVA los define tu país de operación.")
            }
            collapsible
          >
            <div className="flex flex-wrap gap-6">
              <div>
                <span className="text-sm font-medium text-fg flex items-center gap-1.5">
                  {t("Moneda")}
                  <InfoHint
                    srLabel={t("Sobre la moneda")}
                    label={t("Se resuelve desde el país de operación de tu empresa y queda congelada en cada versión. No se edita acá.")}
                  />
                </span>
                <span className="inline-flex items-center h-9 px-3 mt-1 rounded-md border border-border bg-bg-subtle text-sm font-medium text-fg">
                  {currency || t("Sin definir")}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-fg flex items-center gap-1.5">
                  {t("IVA")}
                  <InfoHint
                    srLabel={t("Sobre el IVA")}
                    label={t("La tasa la fija el país de operación, igual que la moneda. Lo único que eliges es si tus montos son netos o ya lo incluyen.")}
                  />
                </span>
                <span className="inline-flex items-center h-9 px-3 mt-1 rounded-md border border-border bg-bg-subtle text-sm font-medium text-fg">
                  {taxRateLabel || t("Sin definir")}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-fg">{t("Tratamiento de IVA")}</span>
                <div className="flex gap-4 mt-2">
                  <label className="inline-flex items-center gap-2 text-sm text-fg cursor-pointer">
                    <input
                      type="radio"
                      name="taxMode"
                      checked={taxMode === "exclusive"}
                      onChange={() => setTaxMode("exclusive")}
                    />
                    {t("+ IVA (montos netos)")}
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-fg cursor-pointer">
                    <input
                      type="radio"
                      name="taxMode"
                      checked={taxMode === "inclusive"}
                      onChange={() => setTaxMode("inclusive")}
                    />
                    {t("IVA incluido")}
                  </label>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-1.5">
                <label htmlFor="dp-origin-account" className="text-sm font-medium text-fg">
                  {t("Cuenta origen")}
                </label>
                <InfoHint
                  srLabel={t("Sobre la cuenta origen")}
                  label={t("Cuenta desde la que transfieres a los repartidores. Aparece como cuenta de cargo en la planilla de pago del banco. Su moneda la deriva el backend de tu país.")}
                />
              </div>
              <input
                id="dp-origin-account"
                className={`${inputCls} max-w-[260px] mt-1`}
                value={originAccount}
                onChange={(e) => setOriginAccount(e.target.value)}
                placeholder={t("Ej: 000123456789")}
              />
            </div>

            {countryCode === "CL" && (
              <div>
                <div className="flex items-center gap-1.5">
                  <label htmlFor="dp-origin-bank" className="text-sm font-medium text-fg">
                    {t("Banco de la cuenta origen")}
                  </label>
                  <InfoHint
                    srLabel={t("Sobre el banco de la cuenta origen")}
                    label={t("Define el banco desde el que transfieres y el formato por defecto de la planilla de pago masivo. Solo aplica a operadores de Chile.")}
                  />
                </div>
                <Select
                  id="dp-origin-bank"
                  className="max-w-[260px] mt-1"
                  value={originBankCode}
                  onChange={(e) => setOriginBankCode(e.target.value)}
                  placeholder={t("Seleccionar banco")}
                >
                  {CHILE_BANKS.map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </Panel>

          {/* 7 · Diagnóstico: herramienta de revisión, no configuración. */}
          <Panel
            icon={Activity}
            title={t("Diagnóstico de cobertura")}
            subtitle={t("Dónde entregaste de verdad y cómo se pagaría cada destino.")}
            hint={t("Cruza los destinos realmente entregados en los últimos 30 días contra esta configuración. Sirve para descubrir comunas con mucho volumen que quizá quieras tarifar aparte.")}
            collapsible
          >
            <Button
              variant="outline"
              onClick={loadCoverage}
              disabled={coverageLoading}
              iconLeft={<RefreshCw className="w-4 h-4" />}
            >
              {coverageLoading ? t("Revisando…") : t("Revisar destinos")}
            </Button>

            {coverage && (
              (coverage.communes ?? []).length === 0 ? (
                <p className="text-xs text-fg-muted">{t("Sin entregas en el período.")}</p>
              ) : (
                <div className="max-h-64 overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-fg-subtle text-xs text-left">
                        <th className="py-1 pr-2">{communeWord}</th>
                        <th className="py-1 pr-2">{t("Entregas")}</th>
                        <th className="py-1 pr-2">{t("Tarifa")}</th>
                        <th className="py-1">{t("Monto")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverage.communes.map((c, i) => (
                        <tr key={i} className="border-t border-border/60">
                          <td className="py-1 pr-2 text-fg">
                            {c.name || "—"}
                            {c.postalCode && (
                              <span className="ml-1 text-xs text-fg-muted">· {c.postalCode}</span>
                            )}
                          </td>
                          <td className="py-1 pr-2 text-fg-muted tabular-nums">{c.deliveries}</td>
                          <td className="py-1 pr-2">
                            <span
                              className={cn(
                                "text-xs px-1.5 py-0.5 rounded",
                                c.pricing === "zone" || c.pricing === "postal"
                                  ? "bg-brand-50 text-brand-700"
                                  : c.pricing === "unconfigured"
                                    ? "bg-status-error/10 text-status-error"
                                    : "bg-bg-subtle text-fg-muted",
                              )}
                            >
                              {c.pricing === "postal"
                                ? t("código postal")
                                : c.pricing === "zone"
                                  ? t("regla propia")
                                  : c.pricing === "unconfigured"
                                    ? t("sin configurar")
                                    : t("por defecto")}
                            </span>
                          </td>
                          <td className="py-1 text-fg tabular-nums">
                            {c.baseCost != null ? fmt(c.baseCost) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </Panel>
        </div>
      </div>

      {/* Barra de guardado fija. Antes el botón estaba al fondo, tras dos pantallas y
          media de scroll, y nada avisaba que quedaban cambios sin guardar. */}
      <div className="sticky bottom-0 z-10 -mx-1 px-1 py-3 bg-bg/95 backdrop-blur border-t border-border flex items-center gap-3 flex-wrap">
        <span className="text-xs text-fg-muted flex items-center gap-1.5 mr-auto">
          {dirty ? (
            <>
              <span className="w-2 h-2 rounded-full bg-status-warning" aria-hidden="true" />
              {t("Cambios sin guardar")}
            </>
          ) : version != null ? (
            <>
              {t("Versión")} <span className="font-medium text-fg">v{version}</span>
              <InfoHint
                align="start"
                srLabel={t("Sobre el versionado")}
                label={t("Cada guardado con cambios crea una versión nueva. Las anteriores se conservan para que las liquidaciones ya hechas sigan cuadrando con la tarifa que estaba vigente ese día.")}
              />
            </>
          ) : null}
        </span>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving}
          iconLeft={<Save className="w-4 h-4" />}
        >
          {saving ? t("Guardando…") : t("Guardar configuración")}
        </Button>
      </div>
    </div>
  );
}
